import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useToast } from '../components/ui/toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Video, Lock, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { token } = useParams<{ token: string }>();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      addToast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        token,
        password,
        password_confirmation: passwordConfirmation
      });
      
      addToast({
        title: "Password reset successful",
        description: "You can now login with your new password.",
        variant: "success"
      });
      
      navigate('/login');
    } catch (error: any) {
      addToast({
        title: "Reset failed",
        description: error.response?.data?.message || 'Failed to reset password. The link may have expired.',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-4 fade-in">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-r from-primary to-slate-600 flex items-center justify-center shadow-lg">
            <Video className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Set new password</h1>
            <p className="text-muted-foreground mt-2">Enter your new password below</p>
          </div>
        </div>

        <Card className="slide-in shadow-2xl border-0 bg-card/50 backdrop-blur-sm">
          <form onSubmit={handleSubmit}>
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl text-center font-semibold">Reset password</CardTitle>
              <CardDescription className="text-center">
                Choose a strong password for your account
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Input
                  type="password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  icon={<Lock className="h-4 w-4" />}
                  className="h-12"
                  minLength={6}
                />
                
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  required
                  disabled={loading}
                  icon={<Lock className="h-4 w-4" />}
                  className="h-12"
                  minLength={6}
                />
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4 pt-6">
              <Button 
                type="submit" 
                className="w-full h-12 text-base"
                variant="gradient"
                disabled={loading}
                loading={loading}
              >
                {loading ? 'Resetting...' : 'Reset password'}
              </Button>
              
              <Link 
                to="/login" 
                className="flex items-center justify-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </Link>
            </CardFooter>
          </form>
        </Card>
        
        <div className="text-center text-xs text-muted-foreground fade-in">
          <p>© 2024 LiveKit. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
